import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { getSchema } from "../data/schemaData";
import PrestatieBlok from "../components/PrestatieBlok";
import { saveActiveTraining, getActiveTraining, clearActiveTraining } from "../lib/activeTraining";
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid
} from "recharts";
import {
  ArrowLeft, Check, Plus, Timer, Dumbbell,
  BarChart2, MoreHorizontal, X, CheckCircle, Calendar, Flame,
  TrendingUp, TrendingDown, Minus, Trash2, ShoppingCart, Info,
  ClipboardList, FileText
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_fitsmith-academy/artifacts/zbh0gz3y_291E0B11-36FD-4420-BAF1-DDEC0FEFC105.png";
const GOLD = "#C9A84C";

// ─── HELPERS ─────────────────────────────────────────────────────────────────
function parseSetsReps(str) {
  if (!str) return { numSets: 3, defaultReps: 10 };
  const match = str.match(/(\d+)(?:-\d+)?\s*[×x]\s*(\d+)/);
  if (match) return { numSets: parseInt(match[1]), defaultReps: parseInt(match[2]) };
  return { numSets: 3, defaultReps: 10 };
}

function parseRustSeconds(rustStr) {
  if (!rustStr) return 90;
  const s = rustStr.trim();
  // 1. "X min Y sec" — meest specifiek eerst
  const minSecMatch = s.match(/(\d+)\s*min\w*\s+(\d+)\s*s/i);
  if (minSecMatch) return parseInt(minSecMatch[1]) * 60 + parseInt(minSecMatch[2]);
  // 2. "X:YY" formaat (bijv. "1:30" = 90s, "2:00" = 120s)
  const colonMatch = s.match(/^(\d+):(\d{2})$/);
  if (colonMatch) return parseInt(colonMatch[1]) * 60 + parseInt(colonMatch[2]);
  // 3. Decimale minuten: "1.5 min" = 90s
  const decMinMatch = s.match(/(\d+\.\d+)\s*min/i);
  if (decMinMatch) return Math.round(parseFloat(decMinMatch[1]) * 60);
  // 4. Hele minuten: "2 min" = 120s
  const minMatch = s.match(/(\d+)\s*min/i);
  if (minMatch) return parseInt(minMatch[1]) * 60;
  // 5. Seconden: "90 sec" of "45sec"
  const secMatch = s.match(/(\d+)\s*sec/i);
  if (secMatch) return parseInt(secMatch[1]);
  // 6. Enkel getal (seconden)
  const numMatch = s.match(/^(\d+)$/);
  if (numMatch) return parseInt(numMatch[1]);
  return 90;
}

function formatTime(seconds) {
  const abs = Math.abs(seconds);
  const m = Math.floor(abs / 60);
  const s = abs % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

function formatRestLabel(seconds) {
  if (seconds < 60) return `${seconds}s`;
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return s === 0 ? `${m}:00` : `${m}:${s.toString().padStart(2, "0")}`;
}

// ─── REST TIMER BAR (sticky bottom) ─────────────────────────────────────────
function RestTimerBar({ timer, onSkip }) {
  if (!timer || timer.seconds <= 0) return null;
  const pct = Math.max(0, (timer.seconds / timer.total) * 100);

  return (
    <div className="fixed bottom-0 left-0 right-0 z-40 animate-slideUp">
      <div className="bg-[#100e05] border-t px-4 pt-4 pb-6" style={{ borderColor: "rgba(201,168,76,0.25)" }}>
        <div className="max-w-2xl mx-auto">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Timer size={16} style={{ color: GOLD }} />
              <span className="text-sm font-bold uppercase tracking-widest" style={{ color: GOLD }}>Rusttijd</span>
            </div>
            <button
              onClick={onSkip}
              data-testid="skip-rest-btn"
              className="flex items-center gap-1.5 text-zinc-400 hover:text-white text-sm font-semibold transition-colors px-3 py-1 rounded-lg border border-white/8 hover:border-white/20"
            >
              Overslaan <X size={13} />
            </button>
          </div>
          <div className="flex items-center gap-5">
            <span className="text-5xl font-bold tabular-nums tracking-tight w-28" style={{ color: GOLD }}>
              {formatTime(timer.seconds)}
            </span>
            <div className="flex-1">
              <div className="h-2.5 bg-[#1a1505] rounded-full overflow-hidden">
                <div
                  className="h-full rounded-full transition-all duration-1000"
                  style={{ width: `${pct}%`, background: `linear-gradient(90deg, ${GOLD}, #D4AF37)` }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── SET ROW ─────────────────────────────────────────────────────────────────
function SetRow({ set, setNum, prevSet, onUpdate, onWeightBlur, onComplete, onDelete, canDelete }) {
  const prevText = prevSet?.kg != null && prevSet?.reps != null
    ? `${prevSet.kg}×${prevSet.reps}`
    : prevSet?.kg != null ? `${prevSet.kg}kg` : null;

  return (
    <div className={`flex items-center gap-1.5 px-3 py-1.5 transition-opacity ${set.completed ? "opacity-50" : ""}`}>
      {/* Set number badge */}
      <div className="w-6 h-6 rounded-md flex items-center justify-center shrink-0 text-xs font-bold"
        style={set.completed
          ? { background: "rgba(201,168,76,0.15)", color: GOLD, border: "1px solid rgba(201,168,76,0.25)" }
          : { background: "#1c1c1c", color: "#71717a", border: "1px solid rgba(255,255,255,0.06)" }}>
        {set.completed ? <Check size={11} /> : setNum}
      </div>

      {/* Vorig */}
      <span className="w-14 shrink-0 text-zinc-400 text-xs font-semibold text-center tabular-nums truncate">
        {prevText || "–"}
      </span>

      {/* Reps stepper: compact */}
      <div className="flex items-center gap-0.5 shrink-0">
        <button
          onPointerDown={e => { e.preventDefault(); onUpdate({ ...set, reps: Math.max(0, (parseInt(set.reps) || 0) - 1) }); }}
          className="w-6 h-8 rounded-md flex items-center justify-center text-sm font-black transition-colors select-none"
          style={{ background: "rgba(255,255,255,0.06)", color: "#71717a" }}
          data-testid={`reps-minus-set-${setNum}`}
        >−</button>
        <span className="w-6 text-center text-white font-black text-sm tabular-nums select-none"
          data-testid={`reps-value-set-${setNum}`}>
          {set.reps}
        </span>
        <button
          onPointerDown={e => { e.preventDefault(); onUpdate({ ...set, reps: (parseInt(set.reps) || 0) + 1 }); }}
          className="w-6 h-8 rounded-md flex items-center justify-center text-sm font-black transition-colors select-none"
          style={{ background: "rgba(255,255,255,0.06)", color: "#71717a" }}
          data-testid={`reps-plus-set-${setNum}`}
        >+</button>
      </div>

      {/* KG stepper: neemt alle resterende ruimte */}
      <div className="flex-1 flex items-center gap-0.5 min-w-0">
        <button
          onPointerDown={e => { e.preventDefault(); onUpdate({ ...set, kg: String(Math.max(0, Math.round(((parseFloat(set.kg) || 0) - 2.5) * 10) / 10)) }); }}
          className="w-7 h-9 rounded-lg flex items-center justify-center text-base font-black transition-all active:scale-90 select-none shrink-0"
          style={{ background: "rgba(255,255,255,0.06)", color: "#71717a" }}
          onMouseEnter={e => { e.currentTarget.style.background = "rgba(201,168,76,0.1)"; e.currentTarget.style.color = GOLD; }}
          onMouseLeave={e => { e.currentTarget.style.background = "rgba(255,255,255,0.06)"; e.currentTarget.style.color = "#71717a"; }}
          data-testid={`kg-minus-set-${setNum}`}
        >−</button>
        <input
          type="number"
          inputMode="decimal"
          min="0"
          step="2.5"
          value={set.kg}
          onChange={e => onUpdate({ ...set, kg: e.target.value })}
          placeholder={prevSet?.kg != null ? String(prevSet.kg) : "0"}
          data-testid={`kg-input-set-${setNum}`}
          className="flex-1 min-w-0 bg-[#111] border border-white/6 rounded-xl px-1 py-2 text-white text-base text-center font-black tabular-nums focus:outline-none focus:bg-[#0f0c02] transition-colors"
          onFocus={e => e.target.style.borderColor = "rgba(201,168,76,0.5)"}
          onBlur={e => { e.target.style.borderColor = ""; onWeightBlur && onWeightBlur(e.target.value); }}
        />
        <button
          onPointerDown={e => { e.preventDefault(); onUpdate({ ...set, kg: String(Math.round(((parseFloat(set.kg) || 0) + 2.5) * 10) / 10) }); }}
          className="w-7 h-9 rounded-lg flex items-center justify-center text-base font-black transition-all active:scale-90 select-none shrink-0"
          style={{ background: "rgba(255,255,255,0.06)", color: "#71717a" }}
          onMouseEnter={e => { e.currentTarget.style.background = "rgba(201,168,76,0.1)"; e.currentTarget.style.color = GOLD; }}
          onMouseLeave={e => { e.currentTarget.style.background = "rgba(255,255,255,0.06)"; e.currentTarget.style.color = "#71717a"; }}
          data-testid={`kg-plus-set-${setNum}`}
        >+</button>
      </div>

      {/* Checkmark button */}
      <button
        onClick={() => !set.completed && onComplete()}
        data-testid={`complete-set-${setNum}`}
        className="w-8 h-9 rounded-lg flex items-center justify-center shrink-0 transition-all active:scale-90 border"
        style={set.completed
          ? { background: "rgba(201,168,76,0.15)", color: GOLD, borderColor: "rgba(201,168,76,0.3)" }
          : { background: "transparent", color: "#52525b", borderColor: "rgba(255,255,255,0.08)" }}
      >
        <Check size={14} />
      </button>

      {/* Delete button */}
      {canDelete && (
        <button
          onClick={onDelete}
          data-testid={`delete-set-${setNum}`}
          className="w-6 h-6 rounded-lg flex items-center justify-center shrink-0 transition-all active:scale-90 text-zinc-700 hover:text-red-400"
        >
          <Trash2 size={12} />
        </button>
      )}
    </div>
  );
}



// ─── PROGRESS POPUP ──────────────────────────────────────────────────────────
function ProgressPopup({ exercise, prevExercise, onClose }) {
  const calcVol = (sets) =>
    (sets || []).reduce((sum, s) => sum + (parseFloat(s.kg) || 0) * (parseInt(s.reps) || 0), 0);

  const curVol = calcVol(exercise.sets);
  const prevVol = calcVol(prevExercise?.sets);

  const pct = prevVol > 0 ? ((curVol - prevVol) / prevVol) * 100 : null;
  const isUp = pct !== null && pct > 0;
  const isDown = pct !== null && pct < 0;
  const isEqual = pct !== null && pct === 0;

  const perSet = exercise.sets.map((s, i) => {
    const cur = (parseFloat(s.kg) || 0) * (parseInt(s.reps) || 0);
    const prev = prevExercise?.sets?.[i]
      ? (parseFloat(prevExercise.sets[i].kg) || 0) * (parseInt(prevExercise.sets[i].reps) || 0)
      : null;
    const diff = prev !== null && prev > 0 ? ((cur - prev) / prev) * 100 : null;
    return { num: i + 1, cur, prev, diff };
  });

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center" onClick={onClose}>
      <div className="absolute inset-0 bg-black/60" />
      <div
        className="relative w-full max-w-lg rounded-t-2xl p-5 pb-8 animate-slideUp"
        style={{ background: "#111", border: "1px solid rgba(201,168,76,0.2)", borderBottom: "none" }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-2">
            <TrendingUp size={15} style={{ color: GOLD }} />
            <span className="text-white font-bold text-sm truncate max-w-[220px]">{exercise.naam}</span>
          </div>
          <button onClick={onClose} className="text-zinc-600 hover:text-white transition-colors">
            <X size={18} />
          </button>
        </div>

        {/* Geen vorige training */}
        {prevVol === 0 && (
          <div className="text-center py-6">
            <Minus size={28} className="mx-auto mb-3 text-zinc-600" />
            <p className="text-zinc-400 text-sm font-semibold">Geen vorige training gevonden</p>
            <p className="text-zinc-600 text-xs mt-1">Voltooi deze training om volgende keer te vergelijken.</p>
          </div>
        )}

        {prevVol > 0 && (
          <>
            {/* Grote percentage indicator */}
            <div className="flex items-center justify-between mb-5 p-4 rounded-xl"
              style={{ background: isUp ? "rgba(34,197,94,0.08)" : isDown ? "rgba(239,68,68,0.08)" : "rgba(201,168,76,0.08)", border: `1px solid ${isUp ? "rgba(34,197,94,0.2)" : isDown ? "rgba(239,68,68,0.2)" : "rgba(201,168,76,0.2)"}` }}>
              <div>
                <p className="text-zinc-500 text-xs mb-1">Voortgang t.o.v. vorige keer</p>
                <div className="flex items-center gap-2">
                  {isUp && <TrendingUp size={20} className="text-green-400" />}
                  {isDown && <TrendingDown size={20} className="text-red-400" />}
                  {isEqual && <Minus size={20} style={{ color: GOLD }} />}
                  <span className="text-2xl font-bold"
                    style={{ color: isUp ? "#4ade80" : isDown ? "#f87171" : GOLD }}>
                    {pct > 0 ? "+" : ""}{pct.toFixed(1)}%
                  </span>
                </div>
              </div>
              <div className="text-right">
                <p className="text-zinc-600 text-xs">Vorig</p>
                <p className="text-zinc-400 text-sm font-bold">{prevVol.toLocaleString("nl-NL")} kg</p>
                <p className="text-zinc-600 text-xs mt-1">Nu</p>
                <p className="text-sm font-bold" style={{ color: curVol > 0 ? GOLD : "#52525b" }}>
                  {curVol > 0 ? `${curVol.toLocaleString("nl-NL")} kg` : "–"}
                </p>
              </div>
            </div>

            {/* Per set vergelijking */}
            <p className="text-zinc-600 text-[10px] uppercase tracking-widest font-bold mb-2">Per set</p>
            <div className="space-y-2">
              {perSet.map(s => (
                <div key={s.num} className="flex items-center justify-between text-xs">
                  <span className="text-zinc-500 w-10">Set {s.num}</span>
                  <span className="text-zinc-600 flex-1 text-center">
                    {s.prev !== null ? `${s.prev.toLocaleString("nl-NL")} → ` : "– → "}
                    <span className="text-zinc-300">{s.cur > 0 ? `${s.cur.toLocaleString("nl-NL")} kg` : "–"}</span>
                  </span>
                  {s.diff !== null ? (
                    <span className="w-14 text-right font-bold"
                      style={{ color: s.diff > 0 ? "#4ade80" : s.diff < 0 ? "#f87171" : GOLD }}>
                      {s.diff > 0 ? "+" : ""}{s.diff.toFixed(0)}%
                    </span>
                  ) : (
                    <span className="w-14 text-right text-zinc-600">nieuw</span>
                  )}
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ─── VOLUME POPUP ─────────────────────────────────────────────────────────────
function VolumePopup({ exercise, onClose }) {
  const sets = exercise.sets;
  const perSet = sets.map((s, i) => {
    const kg = parseFloat(s.kg) || 0;
    const reps = parseInt(s.reps) || 0;
    return { num: i + 1, kg, reps, vol: kg * reps };
  });
  const total = perSet.reduce((sum, s) => sum + s.vol, 0);
  const completedVol = perSet.filter((_, i) => sets[i].completed).reduce((sum, s) => sum + s.vol, 0);

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center" onClick={onClose}>
      <div className="absolute inset-0 bg-black/60" />
      <div
        className="relative w-full max-w-lg rounded-t-2xl p-5 pb-8 animate-slideUp"
        style={{ background: "#111", border: "1px solid rgba(201,168,76,0.2)", borderBottom: "none" }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Flame size={15} style={{ color: GOLD }} />
            <span className="text-white font-bold text-sm truncate max-w-[220px]">{exercise.naam}</span>
          </div>
          <button onClick={onClose} className="text-zinc-600 hover:text-white transition-colors">
            <X size={18} />
          </button>
        </div>

        {/* Per set breakdown */}
        <div className="space-y-2 mb-4">
          {perSet.map(s => (
            <div key={s.num} className="flex items-center justify-between text-sm"
              style={{ opacity: sets[s.num - 1].completed ? 0.5 : 1 }}>
              <span className="text-zinc-500 text-xs">Set {s.num}</span>
              <span className="text-zinc-400 text-xs font-mono">
                {s.kg > 0 ? `${s.kg} kg × ${s.reps} reps` : `– × ${s.reps} reps`}
              </span>
              <span className="font-bold text-xs" style={{ color: s.vol > 0 ? GOLD : "#52525b" }}>
                {s.vol > 0 ? `${s.vol.toLocaleString("nl-NL")} kg` : "–"}
              </span>
            </div>
          ))}
        </div>

        {/* Divider */}
        <div className="h-px mb-4" style={{ background: "rgba(201,168,76,0.15)" }} />

        {/* Totals */}
        <div className="space-y-2">
          {completedVol > 0 && completedVol < total && (
            <div className="flex items-center justify-between">
              <span className="text-zinc-500 text-xs">Gedaan</span>
              <span className="text-zinc-300 text-sm font-bold">{completedVol.toLocaleString("nl-NL")} kg</span>
            </div>
          )}
          <div className="flex items-center justify-between">
            <span className="text-zinc-400 text-sm font-semibold">Totaal volume</span>
            <span className="text-xl font-bold" style={{ color: GOLD }}>
              {total > 0 ? `${total.toLocaleString("nl-NL")} kg` : "Nog geen gewicht"}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── INFO MODAL ───────────────────────────────────────────────────────────────
function InfoModal({ exercise, onClose }) {
  const { user } = useAuth();
  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center" onClick={onClose}>
      <div className="absolute inset-0 bg-black/60" />
      <div
        className="relative w-full max-w-lg rounded-t-2xl p-5 pb-8 animate-slideUp"
        style={{ background: "#111", border: "1px solid rgba(201,168,76,0.2)", borderBottom: "none" }}
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Info size={15} style={{ color: GOLD }} />
            <span className="text-white font-bold text-sm truncate max-w-[240px]">{exercise.naam}</span>
          </div>
          <button onClick={onClose} className="text-zinc-600 hover:text-white transition-colors">
            <X size={18} />
          </button>
        </div>
        <div className="space-y-3">
          {exercise.setsReps && (
            <div className="flex items-center gap-2">
              <span className="text-zinc-500 text-xs uppercase tracking-widest font-bold w-16 shrink-0">Sets</span>
              <span className="text-zinc-200 text-sm font-semibold">{exercise.setsReps}</span>
            </div>
          )}
          {exercise.restSeconds > 0 && (
            <div className="flex items-center gap-2">
              <span className="text-zinc-500 text-xs uppercase tracking-widest font-bold w-16 shrink-0">Rust</span>
              <span className="text-zinc-200 text-sm font-semibold">
                {exercise.restSeconds < 60 ? `${exercise.restSeconds} sec` : `${Math.floor(exercise.restSeconds / 60)}:${String(exercise.restSeconds % 60).padStart(2, "0")} min`}
              </span>
            </div>
          )}
          {exercise.tempo && (
            <div className="flex items-center gap-2">
              <span className="text-zinc-500 text-xs uppercase tracking-widest font-bold w-16 shrink-0">Tempo</span>
              <span className="text-zinc-200 text-sm font-semibold">{exercise.tempo}</span>
            </div>
          )}
          {(exercise.notitie || exercise.notities || exercise.uitvoeringstip) && (
            <div className="pt-3 border-t border-white/6">
              <p className="text-zinc-500 text-xs uppercase tracking-widest font-bold mb-2">Uitvoering</p>
              <p className="text-zinc-300 text-sm leading-relaxed">{exercise.notitie || exercise.notities || exercise.uitvoeringstip}</p>
            </div>
          )}
          {!exercise.notitie && !exercise.notities && !exercise.uitvoeringstip && (
            <p className="text-zinc-600 text-sm italic pt-2">Geen instructies beschikbaar voor deze oefening.</p>
          )}
          <PrestatieBlok naam={exercise.naam} token={user?.token} huidigeSets={exercise.sets || []} />
        </div>
      </div>
    </div>
  );
}

// ─── SCHEMA OVERZICHT MODAL (bekijk schema tijdens training) ─────────────────
function SchemaOverzichtModal({ exercises, sessionNaam, onClose }) {
  return (
    <div className="fixed inset-0 z-[60] flex items-end justify-center" onClick={onClose}>
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
      <div
        className="relative w-full max-w-lg rounded-t-3xl max-h-[85vh] flex flex-col animate-slideUp"
        style={{ background: "#111", border: "1px solid rgba(201,168,76,0.2)", borderBottom: "none" }}
        onClick={e => e.stopPropagation()}
      >
        <div className="px-5 pt-5 pb-4 border-b border-white/6 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ClipboardList size={16} style={{ color: GOLD }} />
            <div>
              <p className="text-white font-bold text-sm">Jouw schema</p>
              <p className="text-zinc-500 text-xs">{sessionNaam} · {exercises.length} oefeningen</p>
            </div>
          </div>
          <button onClick={onClose} data-testid="schema-overzicht-sluiten" className="text-zinc-500 hover:text-white transition-colors"><X size={18} /></button>
        </div>
        <div className="overflow-y-auto flex-1 px-5 py-4 space-y-3 pb-8">
          {exercises.map((ex, i) => (
            <div key={ex.code || i} className="rounded-xl border border-white/6 p-4" style={{ background: "#0d0d0d" }}>
              <div className="flex items-center gap-2 mb-1.5">
                <span className="w-5 h-5 rounded-md flex items-center justify-center text-[10px] font-black shrink-0" style={{ background: "rgba(201,168,76,0.15)", color: GOLD }}>{i + 1}</span>
                <p className="text-white font-bold text-sm">{ex.naam}</p>
              </div>
              <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-zinc-400 pl-7">
                {ex.setsReps && <span><span className="text-zinc-600">Sets:</span> {ex.setsReps}</span>}
                {ex.restSeconds > 0 && <span><span className="text-zinc-600">Rust:</span> {formatRestLabel(ex.restSeconds)}</span>}
                {ex.tempo && <span><span className="text-zinc-600">Tempo:</span> {ex.tempo}</span>}
              </div>
              {ex.notitie && (
                <p className="text-zinc-400 text-xs leading-relaxed mt-2 pl-7 italic">{ex.notitie}</p>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── EXERCISE CARD ───────────────────────────────────────────────────────────
function ExerciseCard({ exercise, prevExercise, onSetsChange, onRestStart, onNoteChange }) {
  const [showVolume, setShowVolume] = useState(false);
  const [showProgress, setShowProgress] = useState(false);
  const [showInfo, setShowInfo] = useState(false);
  const [showNote, setShowNote] = useState(!!exercise.userNote);

  // Auto-open notitieveld zodra een prefilled notitie beschikbaar komt
  useEffect(() => {
    if (exercise.userNote?.trim()) setShowNote(true);
  }, [exercise.userNote]);

  const handleUpdate = (idx, updated) => {
    const next = [...exercise.sets];
    next[idx] = updated;
    onSetsChange(next);
  };

  // Auto-fill gewicht naar volgende lege sets bij verlaten veld
  const handleWeightBlur = (idx, kg) => {
    if (!kg || String(kg).trim() === "" || kg === "0") return;
    const next = [...exercise.sets];
    let changed = false;
    for (let i = idx + 1; i < next.length; i++) {
      if (!next[i].kg || String(next[i].kg).trim() === "" || next[i].kg === "0") {
        next[i] = { ...next[i], kg };
        changed = true;
      }
    }
    if (changed) onSetsChange(next);
  };

  const handleComplete = (idx) => {
    const next = [...exercise.sets];
    next[idx] = { ...next[idx], completed: true };
    onSetsChange(next);
    onRestStart(exercise.restSeconds);
  };

  const handleAddSet = () => {
    const last = exercise.sets[exercise.sets.length - 1];
    onSetsChange([...exercise.sets, { kg: last?.kg ?? "", reps: last?.reps ?? 10, completed: false }]);
  };

  const handleDeleteSet = (idx) => {
    if (exercise.sets.length <= 1) return;
    onSetsChange(exercise.sets.filter((_, i) => i !== idx));
  };

  const completedCount = exercise.sets.filter(s => s.completed).length;
  const allDone = completedCount === exercise.sets.length;

  return (
    <>
      <div
        className="bg-[#0f0f0f] rounded-2xl overflow-hidden mb-4 transition-all border"
        style={{ borderColor: allDone ? "rgba(201,168,76,0.2)" : "rgba(255,255,255,0.05)" }}
      >
        {/* Exercise header */}
        <div className="flex items-center justify-between px-4 py-3.5 border-b border-white/5">
          <div className="flex items-center gap-2.5 min-w-0">
            {allDone && <CheckCircle size={16} style={{ color: GOLD }} className="shrink-0" />}
            <h3 className="font-bold text-base truncate" style={{ color: GOLD }}>{exercise.naam}</h3>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <div
              className="px-2.5 py-1 rounded-md text-sm font-bold"
              style={{ background: "rgba(201,168,76,0.1)", color: "rgba(201,168,76,0.7)" }}
            >
              {completedCount}/{exercise.sets.length}
            </div>
            <button
              onClick={() => setShowInfo(true)}
              className="w-8 h-8 rounded-lg flex items-center justify-center transition-all active:scale-90"
              style={{ background: "rgba(201,168,76,0.08)" }}
              data-testid={`info-btn-${exercise.code}`}
              title="Uitvoering bekijken"
            >
              <Info size={14} style={{ color: "rgba(201,168,76,0.6)" }} />
            </button>
            <button
              onClick={() => setShowNote(v => !v)}
              className="w-8 h-8 rounded-lg flex items-center justify-center transition-all active:scale-90"
              style={{ background: exercise.userNote ? "rgba(201,168,76,0.2)" : "rgba(201,168,76,0.08)" }}
              data-testid={`note-btn-${exercise.code}`}
              title="Opmerking toevoegen"
            >
              <FileText size={14} style={{ color: exercise.userNote ? GOLD : "rgba(201,168,76,0.6)" }} />
            </button>
            <button
              onClick={() => setShowProgress(true)}
              className="w-8 h-8 rounded-lg flex items-center justify-center transition-all active:scale-90"
              style={{ background: "rgba(201,168,76,0.08)" }}
              data-testid={`progress-btn-${exercise.code}`}
            >
              <BarChart2 size={14} style={{ color: "rgba(201,168,76,0.6)" }} />
            </button>
            <button
              onClick={() => setShowVolume(true)}
              className="w-8 h-8 rounded-lg flex items-center justify-center transition-all active:scale-90"
              style={{ background: "rgba(201,168,76,0.08)" }}
              data-testid={`volume-btn-${exercise.code}`}
            >
              <MoreHorizontal size={14} style={{ color: "rgba(201,168,76,0.6)" }} />
            </button>
          </div>
        </div>

        {/* Column headers */}
        <div className="flex items-center gap-1.5 px-3 py-2 border-b border-white/4">
          <div className="w-6 shrink-0" />
          <div className="w-14 shrink-0 text-zinc-600 text-[10px] font-bold uppercase tracking-widest text-center">Vorig</div>
          <div className="w-[66px] shrink-0 text-zinc-600 text-[10px] font-bold uppercase tracking-widest text-center">Reps</div>
          <div className="flex-1 text-zinc-600 text-[10px] font-bold uppercase tracking-widest text-center">kg</div>
          <div className="w-8 shrink-0 flex justify-center">
            <Check size={11} className="text-zinc-600" />
          </div>
        </div>

        {/* Sets */}
        <div>
          {exercise.sets.map((set, i) => (
            <div key={i}>
              <SetRow
                set={set}
                setNum={i + 1}
                prevSet={prevExercise?.sets[i]}
                onUpdate={(updated) => handleUpdate(i, updated)}
                onWeightBlur={(kg) => handleWeightBlur(i, kg)}
                onComplete={() => handleComplete(i)}
                onDelete={() => handleDeleteSet(i)}
                canDelete={exercise.sets.length > 1}
              />
              {i < exercise.sets.length - 1 && (
                <div className="flex items-center gap-2 px-4 py-1.5">
                  <div className="flex-1 h-px" style={{ background: "rgba(201,168,76,0.1)" }} />
                  <span className="text-xs font-bold tabular-nums" style={{ color: "rgba(201,168,76,0.4)" }}>
                    {formatRestLabel(exercise.restSeconds)}
                  </span>
                  <div className="flex-1 h-px" style={{ background: "rgba(201,168,76,0.1)" }} />
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Opmerking bij oefening (zichtbaar voor coach/admin) */}
        {showNote && (
          <div className="px-3 pt-2 pb-1 border-t border-white/4">
            <textarea
              value={exercise.userNote || ""}
              onChange={e => onNoteChange(e.target.value)}
              placeholder="Opmerking bij deze oefening (bijv. voelde zwaar, schouder gevoelig)..."
              rows={2}
              data-testid={`note-input-${exercise.code}`}
              className="w-full bg-[#111] border border-white/8 rounded-xl px-3 py-2 text-white text-sm focus:outline-none focus:border-[#C9A84C]/50 resize-none placeholder-zinc-600"
            />
          </div>
        )}

        {/* Add set button */}
        <button
          onClick={handleAddSet}
          data-testid={`add-set-${exercise.code}`}
          className="w-full py-3.5 flex items-center justify-center gap-2 text-zinc-400 hover:text-white hover:bg-white/2 transition-all border-t border-white/4 text-sm font-semibold"
        >
          <Plus size={15} />
          Voeg set toe ({formatRestLabel(exercise.restSeconds)})
        </button>
      </div>

      {/* Info popup */}
      {showInfo && (
        <InfoModal exercise={exercise} onClose={() => setShowInfo(false)} />
      )}

      {/* Progress popup */}
      {showProgress && (
        <ProgressPopup exercise={exercise} prevExercise={prevExercise} onClose={() => setShowProgress(false)} />
      )}

      {/* Volume popup */}
      {showVolume && (
        <VolumePopup exercise={exercise} onClose={() => setShowVolume(false)} />
      )}
    </>
  );
}

// ─── CUSTOM TOOLTIP VOOR CHART ───────────────────────────────────────────────
function GoldTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-xl px-3 py-2 text-xs border" style={{ background: "#1a1a1a", borderColor: "rgba(201,168,76,0.3)" }}>
      <p className="text-zinc-400 mb-1">{label}</p>
      <p className="font-bold" style={{ color: GOLD }}>{payload[0].value.toLocaleString("nl-NL")} kg</p>
    </div>
  );
}

// ─── COMPLETION SCREEN ───────────────────────────────────────────────────────
function CompletionScreen({ elapsed, sessionNaam, comboKey, sessionId, navigate, exercises, sessionCount, userToken }) {
  const [history, setHistory] = useState(null);

  useEffect(() => {
    if (userToken) {
      fetch(`${API}/api/workout/${comboKey}/${encodeURIComponent(sessionId)}/history`, {
        headers: { Authorization: `Bearer ${userToken}` }
      })
        .then(r => r.ok ? r.json() : null)
        .then(d => d && setHistory(d.logs))
        .catch(() => {});
    }
  }, [comboKey, sessionId, userToken]);

  const totalVolume = exercises.reduce((sum, ex) =>
    sum + ex.sets.reduce((s2, s) => s2 + (parseFloat(s.kg) || 0) * (parseInt(s.reps) || 0), 0), 0
  );
  const completedSets = exercises.reduce((sum, ex) => sum + ex.sets.filter(s => s.completed).length, 0);
  const totalSets = exercises.reduce((sum, ex) => sum + ex.sets.length, 0);

  const ordinal = (n) => n === 1 ? "1e keer" : n === 2 ? "2e keer" : n === 3 ? "3e keer" : `${n}e keer`;

  // Bouw grafiekdata per oefening vanuit de volledige history
  const chartData = history
    ? exercises.map(ex => {
        const points = history.map(log => {
          const logEx = log.exercises?.find(e => e.naam === ex.naam);
          const vol = logEx
            ? logEx.sets.reduce((s, set) => s + (parseFloat(set.kg) || 0) * (parseInt(set.reps) || 0), 0)
            : 0;
          return {
            datum: new Date(log.logged_at).toLocaleDateString("nl-NL", { day: "numeric", month: "short" }),
            vol,
          };
        });
        return { naam: ex.naam, points };
      })
    : [];

  return (
    <div className="min-h-screen bg-[#080808] pb-10">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-[#080808]/95 backdrop-blur-md border-b border-white/5 px-4 py-3 flex items-center gap-3">
        <div className="w-8 h-8 rounded-xl flex items-center justify-center" style={{ background: "rgba(201,168,76,0.15)" }}>
          <CheckCircle size={16} style={{ color: GOLD }} />
        </div>
        <div>
          <p className="text-white font-bold text-sm leading-none">Training voltooid!</p>
          <p className="text-zinc-500 text-xs mt-0.5">{sessionNaam}</p>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-3 pt-5 space-y-4">
        {/* Stats row */}
        <div className="grid grid-cols-3 gap-3">
          {[
            { label: "Tijd", value: formatTime(elapsed) },
            { label: "Volume", value: totalVolume > 0 ? `${totalVolume.toLocaleString("nl-NL")} kg` : "–" },
            { label: sessionCount ? ordinal(sessionCount) : "–", value: "training", flip: true },
          ].map(({ label, value, flip }) => (
            <div key={label} className="rounded-2xl p-4 text-center border border-white/5" style={{ background: "#111" }}>
              {flip ? (
                <>
                  <p className="text-2xl font-bold" style={{ color: GOLD }}>{label}</p>
                  <p className="text-zinc-500 text-xs mt-1">{value}</p>
                </>
              ) : (
                <>
                  <p className="text-2xl font-bold" style={{ color: GOLD }}>{value}</p>
                  <p className="text-zinc-500 text-xs mt-1">{label}</p>
                </>
              )}
            </div>
          ))}
        </div>

        {/* Oefeningen overzicht */}
        <div className="rounded-2xl overflow-hidden border border-white/5" style={{ background: "#111" }}>
          <div className="px-4 py-3 border-b border-white/5 flex items-center gap-2">
            <Dumbbell size={13} style={{ color: GOLD }} />
            <p className="text-white font-bold text-sm">Oefeningen</p>
            <span className="ml-auto text-zinc-600 text-xs">{completedSets}/{totalSets} sets</span>
          </div>
          <div className="divide-y divide-white/4">
            {exercises.map(ex => {
              const vol = ex.sets.reduce((s, set) =>
                s + (parseFloat(set.kg) || 0) * (parseInt(set.reps) || 0), 0);
              return (
                <div key={ex.code} className="px-4 py-3">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-white text-sm font-semibold">{ex.naam}</p>
                    {vol > 0 && (
                      <span className="text-xs font-bold" style={{ color: GOLD }}>
                        {vol.toLocaleString("nl-NL")} kg
                      </span>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {ex.sets.map((s, i) => {
                      const kg = parseFloat(s.kg) || 0;
                      const reps = parseInt(s.reps) || 0;
                      const hasData = kg > 0 || reps > 0;
                      return (
                        <div key={i} className="px-2.5 py-1 rounded-lg text-xs font-mono border"
                          style={s.completed
                            ? { background: "rgba(201,168,76,0.1)", color: GOLD, borderColor: "rgba(201,168,76,0.2)" }
                            : { background: "#1a1a1a", color: "#52525b", borderColor: "rgba(255,255,255,0.06)" }}>
                          {hasData ? `${kg > 0 ? kg + " kg" : "–"} × ${reps}` : `Set ${i + 1}`}
                        </div>
                      );
                    })}
                  </div>
                  {ex.userNote?.trim() && (
                    <p className="text-zinc-400 text-xs italic mt-2 flex items-start gap-1.5">
                      <FileText size={11} className="mt-0.5 shrink-0" style={{ color: GOLD }} />
                      <span>{ex.userNote.trim()}</span>
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Voortgangsgrafieken — altijd zichtbaar */}
        {history !== null && chartData.some(c => c.points.some(p => p.vol > 0)) && (
          <div className="space-y-4">
            <div className="flex items-center gap-2 px-1">
              <TrendingUp size={14} style={{ color: GOLD }} />
              <p className="text-white font-bold text-sm">Voortgang per oefening</p>
            </div>

            {history === null ? (
              <div className="rounded-2xl border border-white/5 p-8 text-center" style={{ background: "#111" }}>
                <p className="text-zinc-600 text-sm">Laden...</p>
              </div>
            ) : (
              chartData.map(({ naam, points }) => {
                const hasData = points.some(p => p.vol > 0);
                if (!hasData) return null;
                const maxVol = Math.max(...points.map(p => p.vol));
                const firstVol = points.find(p => p.vol > 0)?.vol || 0;
                const lastVol = [...points].reverse().find(p => p.vol > 0)?.vol || 0;
                const pct = firstVol > 0 ? ((lastVol - firstVol) / firstVol) * 100 : 0;

                return (
                  <div key={naam} className="rounded-2xl border border-white/5 overflow-hidden" style={{ background: "#111" }}>
                    <div className="px-4 pt-3 pb-2 flex items-center justify-between">
                      <p className="text-white text-sm font-semibold">{naam}</p>
                      <span className="text-xs font-bold px-2 py-0.5 rounded-lg"
                        style={pct >= 0
                          ? { background: "rgba(74,222,128,0.1)", color: "#4ade80" }
                          : { background: "rgba(248,113,113,0.1)", color: "#f87171" }}>
                        {pct >= 0 ? "+" : ""}{pct.toFixed(0)}%
                      </span>
                    </div>
                    <div className="px-2 pb-3">
                      <ResponsiveContainer width="100%" height={130}>
                        <LineChart data={points} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" />
                          <XAxis dataKey="datum" tick={{ fill: "#52525b", fontSize: 10 }} axisLine={false} tickLine={false} />
                          <YAxis domain={[0, maxVol * 1.15]} tick={{ fill: "#52525b", fontSize: 10 }} axisLine={false} tickLine={false} />
                          <Tooltip content={<GoldTooltip />} />
                          <Line
                            type="monotone"
                            dataKey="vol"
                            stroke={GOLD}
                            strokeWidth={2}
                            dot={{ fill: GOLD, r: 3, strokeWidth: 0 }}
                            activeDot={{ fill: GOLD, r: 5, strokeWidth: 0 }}
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                );
              })
            )}

            {/* Nieuw schema knop — vanaf 5e training */}
            {sessionCount >= 5 && (
              <div className="rounded-2xl border p-5" style={{ background: "#0e0c08", borderColor: "rgba(201,168,76,0.2)" }}>
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0 mt-0.5"
                    style={{ background: "rgba(201,168,76,0.12)" }}>
                    <ShoppingCart size={15} style={{ color: GOLD }} />
                  </div>
                  <div className="flex-1">
                    <p className="text-white font-bold text-sm">Klaar voor de volgende stap?</p>
                    <p className="text-zinc-500 text-xs mt-1 leading-relaxed">
                      Je hebt {sessionCount} trainingen voltooid. Een nieuw persoonlijk schema helpt je doorbreken naar een volgend niveau.
                    </p>
                    <button
                      onClick={() => navigate("/bestellen?product=persoonlijk_schema")}
                      data-testid="new-schema-cta"
                      className="mt-3 px-5 py-2.5 rounded-xl font-bold text-xs uppercase tracking-wide transition-all active:scale-95"
                      style={{ background: "linear-gradient(135deg, #C9A84C, #D4AF37)", color: "#000" }}
                    >
                      Nieuw persoonlijk schema aanvragen
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Terug knop */}
        <button
          onClick={() => navigate(`/schema/${comboKey}`)}
          data-testid="back-to-schema-btn"
          className="w-full py-4 rounded-2xl font-bold text-sm uppercase tracking-wide transition-all active:scale-95"
          style={{ background: "linear-gradient(135deg, #C9A84C, #D4AF37)", color: "#000" }}
        >
          Terug naar schema
        </button>
      </div>
    </div>
  );
}

// ─── MAIN COMPONENT ───────────────────────────────────────────────────────────
export default function WorkoutLogger() {
  const { comboKey, sessionId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const savedTrainingRef = useRef((() => {
    const s = getActiveTraining();
    return s && s.route === window.location.pathname ? s : null;
  })());
  const startTimeRef = useRef(savedTrainingRef.current?.startedAt || Date.now());

  const [exercises, setExercises] = useState([]);
  const [prevLog, setPrevLog] = useState(null);
  const [sessionNaam, setSessionNaam] = useState("Training");
  const [elapsed, setElapsed] = useState(0);
  const [restTimer, setRestTimer] = useState(null);
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(true);
  const [sessionCount, setSessionCount] = useState(null);
  const [showSchema, setShowSchema] = useState(false);

  useEffect(() => {
    const load = async () => {
      let sessions = null;
      try {
        const r = await fetch(`${API}/api/schemas/${comboKey}`);
        const d = await r.json();
        if (d.sessions?.length) sessions = d.sessions;
      } catch {}

      const base = getSchema(comboKey);
      const allSessions = sessions || base?.sessions || [];
      const session = allSessions.find(s => s.id === sessionId);

      if (!session) { setLoading(false); return; }
      setSessionNaam(session.naam);

      let prev = null;
      let globalHist = {};
      if (user?.token) {
        try {
          const r = await fetch(`${API}/api/workout/${comboKey}/${encodeURIComponent(sessionId)}/previous`, {
            headers: { Authorization: `Bearer ${user.token}` }
          });
          if (r.ok) prev = await r.json();
          if (prev && !prev.exercises) prev = null;
        } catch {}
        try {
          const rh = await fetch(`${API}/api/workout/exercise-history`, {
            headers: { Authorization: `Bearer ${user.token}` }
          });
          if (rh.ok) globalHist = (await rh.json()).history || {};
        } catch {}
      }
      setPrevLog(prev);

      const exState = session.exercises.map(ex => {
        const { numSets, defaultReps } = parseSetsReps(ex.sets_reps);
        const prevEx = prev?.exercises?.find(pe => pe.code === ex.code);
        const restSeconds = parseRustSeconds(ex.rust);
        return {
          code: ex.code,
          naam: ex.naam,
          restSeconds,
          notitie: ex.notitie || ex.notities || ex.uitvoeringstip || null,
          setsReps: ex.sets_reps || null,
          tempo: ex.tempo || null,
          userNote: globalHist[ex.naam]?.notitie || "",
          sets: Array.from({ length: numSets }, (_, i) => {
            const ps = prevEx?.sets?.[i];
            return {
              kg: ps?.kg != null ? String(ps.kg) : "",
              reps: ps?.reps != null ? ps.reps : defaultReps,
              completed: false,
            };
          }),
        };
      });

      setExercises(savedTrainingRef.current?.state?.exercises?.length
        ? savedTrainingRef.current.state.exercises
        : exState);
      setLoading(false);
    };
    load();
  }, [comboKey, sessionId, user]);

  // Bewaar actieve training zodat de sporter kan wegnavigeren en terugkeren
  useEffect(() => {
    if (loading || done || exercises.length === 0) return;
    saveActiveTraining(window.location.pathname, sessionNaam, startTimeRef.current, { exercises });
  }, [exercises, sessionNaam, loading, done]);

  useEffect(() => {
    const interval = setInterval(() => {
      setElapsed(Math.floor((Date.now() - startTimeRef.current) / 1000));
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (!restTimer || restTimer.seconds <= 0) {
      if (restTimer?.seconds === 0) setRestTimer(null);
      return;
    }
    const t = setTimeout(() => setRestTimer(r => ({ ...r, seconds: r.seconds - 1 })), 1000);
    return () => clearTimeout(t);
  }, [restTimer]);

  const handleRestStart = useCallback((seconds) => {
    setRestTimer({ seconds, total: seconds });
  }, []);

  const handleFinish = async () => {
    setSaving(true);
    try {
      const payload = {
        session_id: sessionId,
        session_naam: sessionNaam,
        training_naam: sessionNaam,
        duration_seconds: elapsed,
        exercises: exercises.map(ex => ({
          code: ex.code,
          naam: ex.naam,
          notitie: ex.userNote?.trim() ? ex.userNote.trim() : null,
          sets: ex.sets.map(s => ({
            kg: s.kg !== "" ? parseFloat(s.kg) : null,
            reps: s.reps !== "" ? parseInt(s.reps) : null,
            completed: s.completed,
          })),
        })),
      };
      const res = await fetch(`${API}/api/workout/${comboKey}`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${user.token}` },
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        const data = await res.json();
        setSessionCount(data.session_count ?? null);
      }
      clearActiveTraining();
      setDone(true);
    } catch {}
    setSaving(false);
  };

  if (done) {
    return <CompletionScreen elapsed={elapsed} sessionNaam={sessionNaam} comboKey={comboKey} sessionId={sessionId} navigate={navigate} exercises={exercises} sessionCount={sessionCount} userToken={user?.token} />;
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-[#080808] flex items-center justify-center">
        <div className="text-zinc-600 text-sm">Laden...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#080808] pb-28">
      {/* ── Sticky Header ── */}
      <div className="sticky top-0 z-30 bg-[#080808]/95 backdrop-blur-md border-b border-white/5">
        <div className="flex items-center justify-between px-4 py-3 max-w-2xl mx-auto">
          <button
            onClick={() => navigate(`/schema/${comboKey}`)}
            className="w-9 h-9 rounded-xl bg-[#141414] border border-white/6 flex items-center justify-center text-zinc-400 hover:text-white transition-colors"
            data-testid="back-btn"
          >
            <ArrowLeft size={17} />
          </button>

          <div className="text-center flex-1 px-4">
            <p className="text-zinc-400 text-xs font-semibold truncate">{sessionNaam}</p>
            <p className="font-bold text-xl tabular-nums tracking-tight leading-none mt-0.5" style={{ color: GOLD }}>
              {formatTime(elapsed)}
            </p>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowSchema(true)}
              data-testid="bekijk-schema-btn"
              title="Bekijk schema"
              className="w-9 h-9 rounded-xl bg-[#141414] border border-white/6 flex items-center justify-center text-[#C9A84C] hover:border-[#C9A84C]/40 transition-colors"
            >
              <ClipboardList size={16} />
            </button>
            <button
              onClick={handleFinish}
              disabled={saving}
              data-testid="finish-workout-btn"
              className="bg-gradient-to-br from-[#C9A84C] to-[#D4AF37] text-black font-bold text-sm px-5 py-2 rounded-xl disabled:opacity-60 active:scale-95 transition-transform"
            >
              {saving ? "..." : "Afronden"}
            </button>
          </div>
        </div>
      </div>

      {/* ── Content ── */}
      <div className="max-w-2xl mx-auto px-3 pt-5">
        <div className="flex items-center gap-4 mb-5">
          <div className="flex items-center gap-1.5 text-zinc-600 text-xs">
            <Calendar size={12} style={{ color: "rgba(201,168,76,0.5)" }} />
            {new Date().toLocaleDateString("nl-NL", { day: "numeric", month: "long", year: "numeric" })}
          </div>
          <div className="flex items-center gap-1.5 text-zinc-600 text-xs">
            <Dumbbell size={12} style={{ color: "rgba(201,168,76,0.5)" }} />
            {exercises.length} oefeningen
          </div>
          {prevLog && (
            <div className="flex items-center gap-1.5 text-zinc-600 text-xs">
              <Timer size={12} style={{ color: "rgba(201,168,76,0.5)" }} />
              Vorig: {new Date(prevLog.logged_at).toLocaleDateString("nl-NL", { day: "numeric", month: "short" })}
            </div>
          )}
        </div>

        {exercises.length === 0 ? (
          <div className="text-center py-20">
            <Dumbbell size={36} className="text-zinc-700 mx-auto mb-3" />
            <p className="text-zinc-500 text-sm">Sessie niet gevonden</p>
            <button onClick={() => navigate(`/schema/${comboKey}`)} className="btn-course-outline text-xs px-4 py-2 mt-4">
              Terug naar schema
            </button>
          </div>
        ) : (
          exercises.map((ex, exIdx) => {
            const prevEx = prevLog?.exercises?.find(pe => pe.code === ex.code);
            return (
              <ExerciseCard
                key={ex.code}
                exercise={ex}
                prevExercise={prevEx}
                onSetsChange={(sets) =>
                  setExercises(prev => {
                    const next = [...prev];
                    next[exIdx] = { ...next[exIdx], sets };
                    return next;
                  })
                }
                onNoteChange={(note) =>
                  setExercises(prev => {
                    const next = [...prev];
                    next[exIdx] = { ...next[exIdx], userNote: note };
                    return next;
                  })
                }
                onRestStart={handleRestStart}
              />
            );
          })
        )}

        <div className="flex items-center justify-center gap-2 mt-8 mb-4 opacity-30">
          <img src={LOGO_URL} alt="FitSmith" className="h-5 w-5 object-contain" />
          <span className="text-zinc-600 text-xs font-bold tracking-wide">FitSmith</span>
        </div>
      </div>

      <RestTimerBar timer={restTimer} onSkip={() => setRestTimer(null)} />

      {showSchema && (
        <SchemaOverzichtModal
          exercises={exercises}
          sessionNaam={sessionNaam}
          onClose={() => setShowSchema(false)}
        />
      )}
    </div>
  );
}
